/**
 * Flyer free-form attachments for POST /rag/generate-media (flyer edit mode).
 */

export interface FlyerMediaAttachment {
    id: string;
    file?: File;
    previewUrl: string;
    url?: string;
    s3_key?: string;
    filename: string;
    fromAsset?: boolean;
    /** Deal this attachment's image belongs to, when known — enables the
     *  backend's S3-unavailable fallback to that deal's data. */
    dealId?: string;
}

export interface FlyerReferenceUploadItem {
    s3_key: string;
    url: string;
    filename: string;
    content_type: string;
}

export interface FlyerReferenceUploadResponse {
    session_id: string;
    folder_prefix?: string;
    uploads: FlyerReferenceUploadItem[];
    primary_s3_key?: string | null;
    primary_url?: string | null;
    /** Present when `sourceS3Key` couldn't be read (missing/access-denied/dead
     *  presigned URL) and `dealId` was supplied — the caller should fall back
     *  to rendering `deal_data` (PII-stripped) instead of an image. */
    s3_available?: boolean;
    s3_error?: string | null;
    fallback?: boolean;
    deal_data?: Record<string, unknown> | null;
}

export async function uploadFlyerReferenceImages(
    apiBase: string,
    headers: Record<string, string>,
    params: {
        sessionId?: string | null;
        files?: File[];
        sourceS3Key?: string | null;
        presignedUrl?: string | null;
        dealId?: string | null;
    },
): Promise<FlyerReferenceUploadResponse> {
    const form = new FormData();
    if (params.sessionId) {
        form.append("session_id", params.sessionId);
    }
    if (params.sourceS3Key) {
        form.append("source_s3_key", params.sourceS3Key);
    }
    if (params.presignedUrl) {
        form.append("presigned_url", params.presignedUrl);
    }
    if (params.dealId) {
        form.append("deal_id", params.dealId);
    }
    for (const f of params.files ?? []) {
        form.append("files", f);
    }
    const res = await fetch(`${apiBase}/rag/flyer-references/upload`, {
        method: "POST",
        headers: { ...headers },
        body: form,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = (err as { detail?: { message?: string } | string })?.detail;
        const msg =
            (typeof detail === "object" && detail && "message" in detail && detail.message)
            || (typeof detail === "string" ? detail : null)
            || "Upload failed";
        throw new Error(msg);
    }
    return res.json() as Promise<FlyerReferenceUploadResponse>;
}
